import {
  STRATEGY_GENERATION_REQUEST_KINDS,
  describeStrategyGenerationRequest,
  normalizeStrategyNextAction,
} from "../utils/strategyNextAction";

/**
 * Whether the planner may OFFER the full-strategy request path for the loaded
 * campaign, and what to call it.
 *
 * The verdict is the backend's: the canonical next-action read resolves a
 * `generationRequest` block from the artifact decision (CREATE_STRATEGY for a
 * campaign with nothing saved, REFRESH_STRATEGY for a saved artifact whose
 * decision does not refuse a fresh request, refused otherwise). This module
 * no longer keeps a list of states it guesses are requestable.
 *
 * None of this is a generation permission. The operations POST and its
 * execution core decide whether a request may run (including the client's
 * DRAFT-only rule); a refusal there is rendered as itself.
 */

// Compatibility only: a backend that predates the resolved affordance sends no
// `generationRequest` block. Until it is live everywhere, the previously
// accepted state list (client #490) stands in — and is never consulted once
// the block is present. Delete when the resolving backend is deployed.
const LEGACY_REQUESTABLE_ARTIFACT_STATES = new Set([
  "NO_STRATEGY", "READY_FOR_REVIEW", "APPROVED",
  "STRATEGY_APPROVED_AWAITING_CAMPAIGN_APPROVAL",
  "ARTIFACT_EVIDENCE_REQUIRED", "GOVERNANCE_REVIEW_REQUIRED",
]);

const UNAVAILABLE = Object.freeze({ available: false, kind: null, label: null, reason: "NEXT_ACTION_UNAVAILABLE", source: "none" });

// `source` names who decided: "backend" when the canonical envelope carried
// the block, "legacy_fallback" when this module inferred it for an older
// backend. Client-facing copy keys on the backend's own verdict only.
export const resolveFullStrategyRequest = (nextAction, campaignId) => {
  const action = normalizeStrategyNextAction(nextAction);
  if (!action || String(action.campaignId) !== String(campaignId)) return { ...UNAVAILABLE };
  const request = describeStrategyGenerationRequest(action);
  if (request.reason !== "GENERATION_REQUEST_NOT_ADVERTISED") return { ...request, source: "backend" };
  const available = LEGACY_REQUESTABLE_ARTIFACT_STATES.has(action.state);
  return {
    available,
    kind: !available
      ? null
      : action.state === "NO_STRATEGY"
        ? STRATEGY_GENERATION_REQUEST_KINDS.CREATE_STRATEGY
        : STRATEGY_GENERATION_REQUEST_KINDS.REFRESH_STRATEGY,
    label: null,
    reason: available ? "LEGACY_STATE_LIST" : `STATE_${action.state}`,
    source: "legacy_fallback",
  };
};

/**
 * The client notice for a campaign whose canonical decision advertises its
 * FIRST strategy. The backend's client-safe NO_STRATEGY notice says the Cphere
 * team will prepare the strategy; beside an enabled request control that is
 * the wrong instruction, so the affordance the backend advertised is
 * explained in client terms. Rendered when and only when the canonical
 * envelope itself resolved CREATE_STRATEGY — never from a local fallback or
 * from campaign state — and it promises no approval and claims no work in
 * progress.
 */
export const FIRST_STRATEGY_CLIENT_NOTICE = Object.freeze({
  headline: "No media strategy has been generated yet.",
  detail: "",
  nextAction: "Select Generate Media Strategy to create the first draft for review.",
});

export const resolveClientStrategyNotice = (canonicalNotice, request) => {
  if (
    request?.available === true &&
    request.kind === STRATEGY_GENERATION_REQUEST_KINDS.CREATE_STRATEGY &&
    request.source === "backend"
  ) {
    return {
      ...(canonicalNotice || {}),
      headline: FIRST_STRATEGY_CLIENT_NOTICE.headline,
      detail: FIRST_STRATEGY_CLIENT_NOTICE.detail,
      nextAction: FIRST_STRATEGY_CLIENT_NOTICE.nextAction,
      requiresOperatorAction: true,
      tone: "action",
      basis: "canonical_generation_request_create_strategy",
    };
  }
  return canonicalNotice || null;
};

export const canRequestFullStrategy = (nextAction, campaignId) =>
  resolveFullStrategyRequest(nextAction, campaignId).available;

export const STRATEGY_REQUEST_UNAVAILABLE =
  "Current strategy status is unavailable. Reload the campaign before requesting a strategy.";
